import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./styles/stylesheet.css";

import { useFetchData } from "./utils/data/useFetchData.js";
import { resolvePath } from "./utils/nav/path";
import { updateAvatarContext, initAvatarSession, setAvatarSceneUrl, interruptAvatar } from "./utils/avatarFrame.js";
import buildTheme from "./utils/buildTheme.js";

import Home from "./pages/Home.jsx";
import LoginModal from "./components/nav/LoginModal.jsx";
import ProtectedRoute from "./components/nav/ProtectedRoute.jsx";
import NotFound from "./components/NotFound.jsx";
import { resolveLayout, LAYOUTS, DEFAULT_LAYOUT } from "./components/layouts/index.js";

const ProjectDetail = lazy(() => import("./pages/ProjectDetail.jsx"));
const InternalProjectDetail = lazy(() => import("./pages/InternalProjectDetail.jsx"));
const Detail = lazy(() => import("./components/templates/home/Detail.jsx"));
const V2Detail = lazy(() => import("./v2/templates/V2Detail.jsx"));
const AlertsAdmin = lazy(() => import("./pages/AlertsAdmin.jsx"));

import { useLocale } from "./state/useLocale";
import { useTranslations } from "./state/useTranslations";
import { LocaleContext } from "./state/LocaleContext.js";

import { prepMapPayload } from "./utils/prepMapPayload.js";

export default function App() {
  // Helper to strip HTML tags and decode entities to plain text
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.replace(/\s+/g, ' ').trim();
  };

  // --- Data hooks (leave these first) ---
  const {
    data: pages,
    loading: loadingPages,
    error: errorPages,
  } = useFetchData("pages");

  const {
    data: groups,
    loading: loadingGroups,
    error: errorGroups,
  } = useFetchData("groups");

  const {
    data: statuses,
    loading: loadingStatuses,
    error: errorStatuses,
  } = useFetchData("status");

  const {
    data: systemPages,
    loading: loadingSystemPages,
    error: errorSystemPages,
  } = useFetchData("system_pages");

  const [mapData, setMapData] = useState(null);
  const [loadingMapData, setLoadingMapData] = useState(true);
  const [errorMapData, setErrorMapData] = useState(null);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const res = await fetch("/api/map-data");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const apiData = await res.json();

        // Use shared prepMapPayload to normalize the data
        const { settings, statuses, projects, categories, issues } =
          prepMapPayload(apiData);

        setMapData({ settings, statuses, projects, categories, issues });
      } catch (err) {
        console.error("[App] map-data fetch failed:", err);
        setErrorMapData(err.message);
      } finally {
        setLoadingMapData(false);
      }
    };

    fetchMapData();
  }, []);

  // --- Other state / params ---
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [showLogin, setShowLogin] = useState(false);
  const { groupSlug, "*": slugPath } = useParams();
  const location = useLocation();

  // --- Ready check ---
  const isReady =
    !loadingPages &&
    !loadingGroups &&
    !loadingStatuses &&
    !loadingMapData &&
    !loadingSystemPages &&
    !errorPages &&
    !errorGroups &&
    !errorStatuses &&
    !errorMapData;

  // --- Group / page resolution ---
  // For ProjectDetail routes, use data from ProjectDetail component (loaded from API)
  // For other routes (Home, etc), use data from global App state
  // Helper to check if current route is a ProjectDetail page
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const hasDetailPagePath = location.pathname !== '/' && pathSegments.length >= 2;

  const page = hasDetailPagePath && window._currentProjectDetailPage
    ? window._currentProjectDetailPage
    : (isReady && groupSlug && groups?.rows?.length && pages?.length
        ? resolvePath(groupSlug, slugPath, groups.rows, pages) || {}
        : {});

  const group = hasDetailPagePath && window._currentProjectDetailGroup
    ? window._currentProjectDetailGroup
    : (isReady && groupSlug
        ? groups.rows.find((g) => g.slug === groupSlug) || null
        : null);

  // --- Map categories ---
  const categories = mapData?.categories || {};
  const settings = mapData?.settings || {};
  const mapStatuses = statuses?.rows || [];

  // Wire avatar scene URL from DB settings (must happen before first getAvatarFrame call)
  useEffect(() => {
    if (settings?.avatar?.avatarUrl) setAvatarSceneUrl(settings.avatar.avatarUrl);
  }, [settings?.avatar?.avatarUrl]);

  // Set CSS custom properties from theme
  useEffect(() => {
    const theme = buildTheme(settings?.theme?.primary, settings?.theme?.accent);
    const root = document.documentElement.style;
    root.setProperty("--theme-primary", theme.primary);
    root.setProperty("--theme-primary-dark", theme.primaryDark);
    root.setProperty("--theme-dark-bg", theme.darkBg);
    root.setProperty("--theme-card-bg", theme.cardBg);
    root.setProperty("--theme-border-dark", theme.borderDark);
    root.setProperty("--theme-accent", theme.accent);
    root.setProperty("--octane-link", theme.primary);
  }, [settings?.theme?.primary, settings?.theme?.accent]);

  // Set meta description from settings
  useEffect(() => {
    const cityName = settings?.city?.name;
    const desc = cityName
      ? `${cityName} city project tracker — view project status, locations, and details.`
      : "Municipal city project tracker — view project status, locations, and details.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, [settings?.city?.name]);

  // --- Locale setup ---
  const { locale, setLocale } = useLocale();
  const translations = useTranslations(locale);
  const t = (entityType, entityId, fieldName, fallback) => {
    const key = `${entityType}:${entityId}:${fieldName}`;
    return translations[key] || fallback;
  };
  const tUI = (name, fallback) => t("ui", 0, name, fallback);

  // --- OM Context Integration ---

  // Initialize context on app load with baseline info
  useEffect(() => {
    // Import and expose OMContext and eventBus globally
    import("@om/collector-sdk").then(({ OMContext, eventBus }) => {
      window.OMContext = OMContext;
      window.eventBus = eventBus;

      // Get or generate session ID
      let sessionId = sessionStorage.getItem('om_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('om_session_id', sessionId);
      }

      // Set app metadata
      try {
        OMContext.set('app', {
          name: 'casa-grande-project-tracker',
          version: '1.0.0',
          initialized: Date.now()
        });

        // Set initial context with baseline info
        const initialContext = {
          locale: locale || 'en',
          app: 'project-tracker',
          sessionId: sessionId,
          route: location.pathname,
          timestamp: Date.now()
        };

        OMContext.set('context', initialContext);
        OMContext.setAppStatus('tracker', 'ready');
      } catch (err) {
        console.warn('[OMContext] Failed to initialize:', err);
      }
      // Register session with avatar server so context updates are accepted
      initAvatarSession({ app: 'Project Tracker', locale: locale || 'en-US' }, {});

      // Belt-and-suspenders: subscribe to every context:changed and forward to
      // avatar API. This catches any publish site that doesn't call updateAvatarContext
      // directly. eventBus is definitely available here (we just set it above).
      eventBus.subscribe('context:changed', (ctx) => {
        updateAvatarContext(ctx);
      });

      // Heartbeat: re-push current context every 30s so the avatar never goes stale
      // even if a publish was missed due to timing, error, or future code changes.
      const heartbeatId = setInterval(() => {
        const ctx = OMContext.get('context');
        if (ctx && Object.keys(ctx).length > 0) updateAvatarContext(ctx);
      }, 30000);

      // Stash cleanup handle for teardown
      window._avatarHeartbeatId = heartbeatId;
    });
    return () => {
      if (window._avatarHeartbeatId) {
        clearInterval(window._avatarHeartbeatId);
        delete window._avatarHeartbeatId;
      }
    };
  }, []); // Only run once on mount

  // Update locale in OMContext when it changes (metadata only — does NOT
  // publish context:changed because locale is not a navigation event)
  useEffect(() => {
    if (locale && window.OMContext) {
      try {
        window.OMContext.updateContext('locale', locale);
      } catch (err) {
        console.warn('[OMContext] Failed to update locale:', err);
      }
    }
  }, [locale]);

  // Stop the avatar talking/listening on every route change — now that
  // navigation to Detail no longer hard-reloads (see MapView's onNavigate),
  // the avatar iframe survives page changes, so a mid-speech interrupt is
  // needed on navigation the same way panel-close already sends one.
  const isFirstRouteRef = useRef(true);
  useEffect(() => {
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return;
    }
    interruptAvatar();
  }, [location.pathname]);

  // --- Context updates: session start + deliberate navigation only ---
  // Never reacts to data settling, re-renders, or locale changes.
  // Map popup open/close is handled by MapView.jsx.
  // Detail page loads are handled by the projectDetailLoaded event below.
  const prevPathnameRef = useRef(null); // null = initial context not yet set

  useEffect(() => {
    if (!isReady || !window.OMContext || !window.eventBus) return;

    const isInitial = prevPathnameRef.current === null;
    const pathnameChanged = location.pathname !== prevPathnameRef.current;

    // Only act on (a) first run after data is ready, or (b) actual navigation
    if (!isInitial && !pathnameChanged) return;

    prevPathnameRef.current = location.pathname;

    // !!! DO NOT TOUCH THIS SKIP CONDITION LIGHTLY !!!
    // Every route whose own component sets avatar context (Detail.jsx via
    // /detail, or CMS group/page routes via the projectDetailLoaded event)
    // MUST be excluded here, or this generic reset silently stomps the real
    // per-project context moments after it's set — this exact regression
    // (untraceable in logs beyond "Type: unknown" immediately followed by
    // "Type: map") broke the avatar's basic context chain in production once
    // already. /detail is a flat, single-segment route so it needs its own
    // explicit check alongside the >=2-segment CMS group/page check.
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (location.pathname === '/detail' || (location.pathname !== '/' && pathParts.length >= 2)) return;

    // Home / map page — send a clean context so the avatar knows there is no
    // selected project. Do NOT spread currentContext; stale project fields
    // (octaneProjectPageId, etc.) must not bleed across navigation.
    const cityName = settings?.city?.name || 'Casa Grande';
    const [cityLng, cityLat] = settings?.city?.center || [];
    const contextUpdate = {
      route: location.pathname,
      timestamp: Date.now(),
      type: 'map',
      title: `${cityName} City Project Tracker`,
      description: `The user is viewing the ${cityName} City Project Tracker interactive map. No specific project is currently selected. Help the user explore the City's projects.`,
      octaneProjectPageId: null,
      octaneProjectGroupId: null,
      octaneProjectName: null,
      octaneProjectDetails: null,
      octaneCategoryLabel: null,
      cityLat: cityLat ?? null,
      cityLng: cityLng ?? null,
    };

    window.OMContext.set('context', contextUpdate);
    window.eventBus.publish('context:changed', contextUpdate);
    updateAvatarContext(contextUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, location.pathname]);

  // Listen for ProjectDetail data loaded event
  useEffect(() => {
    const handleProjectDetailLoaded = (event) => {
      const { page: detailPage, group: detailGroup } = event.detail;

      try {
        const pageTitle = detailPage.title === 'Home' && detailGroup?.title
          ? `${detailGroup.title} ${detailPage.title}`
          : detailPage.title;
        const pageDescription = stripHtml(detailPage.description) || '';
        const pageUpdate = {
          type: 'page',
          title: pageTitle,
          description: pageDescription,
          route: location.pathname,
          timestamp: Date.now(),
          // octaneProject* fields so avatar receives context in the format it expects
          octaneProjectPageId: String(detailPage.id),
          octaneProjectGroupId: String(detailGroup?.id || detailPage.id),
          octaneProjectName: pageTitle,
          octaneProjectDetails: pageDescription,
          octaneCategoryLabel: detailGroup?.title || '',
          // structured page/group/project data
          page: {
            id: detailPage.id,
            slug: detailPage.slug,
            title: detailPage.title
          },
          group: {
            id: detailGroup?.id,
            slug: detailGroup?.slug,
            title: detailGroup?.title
          },
          project: {
            id: detailPage.id,
            name: pageTitle,
            slug: detailPage.slug
          }
        };

        window.OMContext.set('context', pageUpdate);
        window.eventBus.publish('context:changed', pageUpdate);
        updateAvatarContext(pageUpdate);
      } catch (err) {
        console.warn('[OMContext] Failed to update context from ProjectDetail:', err);
      }
    };

    window.addEventListener('projectDetailLoaded', handleProjectDetailLoaded);
    return () => window.removeEventListener('projectDetailLoaded', handleProjectDetailLoaded);
  }, [location.pathname]);

  // --- JSX ---
  const Layout = resolveLayout(DEFAULT_LAYOUT);
  // /detail (below) is a hardcoded route, not resolved through Home.jsx's
  // TEMPLATES registry, so it needs its own v1/v2 split here instead of
  // picking it up for free the way every other page does.
  const isV2 = Layout === LAYOUTS.v2;

  return (
    <LocaleContext.Provider value={{ locale, setLocale, translations, t, tUI }}>
      <Layout
        navProps={{
          groups,
          pages,
          statuses,
          group,
          page,
          role,
          setRole,
          showLogin,
          setShowLogin,
          isReady,
          settings,
        }}
      >
          <Suspense fallback={null}>
          {/* Wait for system_pages before matching routes — otherwise a cold
              load of e.g. /expert races ahead of the fetch, finds no literal
              route for it yet, falls through to the :groupSlug catch-all
              below, and gets redirected into a 404 at /expert/home. */}
          {loadingSystemPages ? null : (
          <Routes>
            <Route
              index
              element={
                <Home
                  key={loadingMapData ? "loading" : "ready"}
                  settings={mapData?.settings}
                  statuses={statuses?.rows}
                  categories={mapData?.categories}
                  homepageTemplate={mapData?.settings?.homepageTemplate}
                  templateOverride={
                    (systemPages?.rows || []).find(p => p.slug === "home" && p.is_active)?.template
                  }
                />
              }
            />
            {/* DB-driven system page routes */}
            {(systemPages?.rows || [])
              .filter(p => p.is_active && p.slug && p.slug !== "home")
              .map(p => (
                <Route
                  key={p.slug}
                  path={p.slug}
                  element={
                    <Home
                      settings={mapData?.settings}
                      statuses={statuses?.rows}
                      categories={mapData?.categories}
                      homepageTemplate={mapData?.settings?.homepageTemplate}
                      templateOverride={p.template}
                    />
                  }
                />
              ))
            }
            {/* Any sub-path under a system page slug (e.g. a stale /projects/home
                link — system pages never had a real "/home" sub-route) belongs to
                that page, not to ProjectDetail's content-group lookup below —
                "projects" etc. can never be a real group slug (enforced by the
                reserved-slug DB trigger), so without this it fell through to
                :groupSlug/*, failed the group lookup, and overshot all the way to
                site home instead of landing on the next real link in the chain. */}
            {(systemPages?.rows || [])
              .filter(p => p.is_active && p.slug && p.slug !== "home")
              .map(p => (
                <Route key={`${p.slug}-wild`} path={`${p.slug}/*`} element={<Navigate to={`/${p.slug}`} replace />} />
              ))
            }
            <Route
              path="detail"
              element={
                isV2 ? (
                  <V2Detail
                    settings={mapData?.settings}
                    statuses={statuses?.rows}
                    categories={mapData?.categories}
                  />
                ) : (
                  <Detail
                    settings={mapData?.settings}
                    statuses={statuses?.rows}
                    categories={mapData?.categories}
                  />
                )
              }
            />
            {/* Same reserved-slug overshoot gap as the system-page wildcards above
                — "detail" can never be a real group slug either. */}
            <Route path="detail/*" element={<Navigate to="/detail" replace />} />
            <Route path=":groupSlug" element={<Navigate to="home" replace />} />
            <Route path=":groupSlug/*" element={<ProjectDetail settings={mapData?.settings} />} />
            <Route
              path="internal/home"
              element={
                <ProtectedRoute allowedRoles={["admin", "editor"]}>
                  <InternalProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/alerts"
              element={
                <ProtectedRoute allowedRoles={["admin", "editor"]}>
                  <AlertsAdmin />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          )}
          </Suspense>
      </Layout>
    </LocaleContext.Provider>
  );
}
